import { useCallback, useEffect, useState } from "react";
import { Star, Instagram, Facebook, Globe, Music2, PenLine } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { api, imgUrl } from "@/lib/api";
import { Reveal, Overline } from "@/components/site/Reveal";
import { ReviewForm } from "./ReviewForm";

const KEYS = ["cleanliness", "accuracy", "checkin", "communication", "location", "value"];
const SOC = { instagram: [Instagram, "https://instagram.com/"], facebook: [Facebook, "https://facebook.com/"], tiktok: [Music2, "https://tiktok.com/@"], website: [Globe, ""] };

const socialHref = (k, v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v)) return v;
  const h = v.replace(/^@/, "");
  return k === "website" ? `https://${h}` : `${SOC[k][1]}${h}`;
};

export const ReviewCard = ({ r }) => {
  const { t } = useI18n();
  const [more, setMore] = useState(false);
  const long = (r.text || "").length > 260;
  return (
    <div className="flex h-full flex-col rounded-3xl bg-white/[0.06] p-6 ring-1 ring-white/10" data-testid={`review-card-${r.id}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gold font-display text-xl text-forest">{(r.author || "?").slice(0, 1)}</div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{r.author}</p>
          <p className="truncate text-xs text-[#C6D3CA]">{r.location || (r.source === "airbnb" ? t("rv.fromAirbnb") : t("rv.guest"))}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 text-xs text-[#C6D3CA]">
        <span className="flex">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className={`h-3 w-3 ${i < (r.rating || 5) ? "fill-gold text-gold" : "text-white/30"}`} />)}</span>
        <span>· {r.date}</span>
      </div>
      <p className={`mt-3 whitespace-pre-line text-[15px] leading-relaxed text-[#F1F5F2] ${!more && long ? "line-clamp-5" : ""}`}>{r.text}</p>
      {long && <button onClick={() => setMore(!more)} className="mt-2 self-start text-sm font-semibold underline underline-offset-4">{more ? t("about.less") : t("about.more")}</button>}
      {r.media?.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          {r.media.map((m, i) => (
            <a key={i} href={imgUrl(m.url)} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden rounded-xl bg-black/30">
              {m.type === "video" ? <video src={imgUrl(m.url)} className="h-full w-full object-cover" muted playsInline preload="metadata" /> : <img src={imgUrl(m.url)} alt="" loading="lazy" className="h-full w-full object-cover" />}
            </a>
          ))}
        </div>
      )}
      {r.social && Object.entries(r.social).some(([, v]) => v) && (
        <div className="mt-4 flex gap-2">
          {Object.entries(r.social).filter(([k, v]) => v && SOC[k]).map(([k, v]) => {
            const I = SOC[k][0];
            return <a key={k} href={socialHref(k, v)} target="_blank" rel="noopener noreferrer nofollow" className="rounded-full border border-white/20 p-2 hover:bg-white/10" aria-label={k}><I className="h-4 w-4" /></a>;
          })}
        </div>
      )}
    </div>
  );
};

export const Reviews = ({ prop }) => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const sr = prop?.sub_ratings || {};
  const load = useCallback(async (skip) => {
    try {
      const { data } = await api.get(`/properties/${prop.slug}/reviews?skip=${skip}&limit=${skip ? 12 : 6}`);
      const list = Array.isArray(data?.items) ? data.items : [];
      setItems((cur) => (skip ? [...cur, ...list] : list));
      setTotal(data?.total ?? 0);
    } catch (e) { /* keep section usable */ }
  }, [prop.slug]);
  useEffect(() => { load(0); }, [load]);
  if (!prop?.rating && !items.length) return null;
  return (
    <section id="reviews" data-testid="reviews-section" className="bg-forest text-paper">
      <div className="mx-auto max-w-[1280px] px-5 py-24 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Overline light>{t("r.title")}</Overline>
            <p data-testid="overall-rating" className="mt-4 font-display text-[120px] leading-none tracking-tight sm:text-[160px]">{prop.rating}</p>
            <div className="mt-2 flex gap-1">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-5 w-5 fill-gold text-gold" />)}</div>
            <p className="mt-4 text-[#DCE6DF]">{t("rv.count", { n: total })}</p>
            <button data-testid="write-review-button" onClick={() => setOpen(true)} className="mt-8 inline-flex items-center gap-2 rounded-full bg-paper px-6 py-3 text-sm font-semibold text-forest transition-transform hover:-translate-y-0.5">
              <PenLine className="h-4 w-4" /> {t("rv.write")}
            </button>
          </Reveal>
          <div className="grid content-start gap-x-10 gap-y-6 sm:grid-cols-2 lg:col-span-7" data-testid="sub-ratings">
            {KEYS.filter((k) => sr[k]).map((k, i) => (
              <Reveal key={k} delay={i * 0.05}>
                <div className="flex justify-between text-sm"><span className="text-[#DCE6DF]">{t(`r.${k}`)}</span><span className="font-semibold">{sr[k]}</span></div>
                <div className="mt-2 h-[3px] overflow-hidden rounded-full bg-white/15"><div className="h-full rounded-full bg-gold" style={{ width: `${(Number(sr[k]) / 5) * 100}%` }} /></div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="reviews-list">
          {items.map((r) => <ReviewCard key={r.id} r={r} />)}
        </div>
        {items.length < total && (
          <div className="mt-10 text-center">
            <button data-testid="reviews-load-more" onClick={() => load(items.length)} className="rounded-full border border-white/30 px-8 py-3 text-sm font-semibold transition-colors hover:bg-white hover:text-forest">
              {t("rv.all")} ({items.length}/{total})
            </button>
          </div>
        )}
      </div>
      <ReviewForm open={open} onOpenChange={setOpen} slug={prop.slug} onDone={() => load(0)} />
    </section>
  );
};
